// File: src/pages/DanielReport.js
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import A from "../data/danielReportDataA";
import B from "../data/danielReportDataB";
import "./DanielReport.css";

const PASSWORD_HASH =
  "60baccdba1b922e1c550458371e1e03503a69c16db503eac5c1e30719bcebc77";
const STORAGE_KEY = "danielReportUnlocked";

async function sha256Hex(text) {
  const data = new TextEncoder().encode(text);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const TAGS = { signal: "Signal", nuance: "Nuance", pr: "PR", bs: "BS", unv: "Unverified" };
const TAGDESC = {
  signal: "true, sourced, and it matters",
  nuance: "true but incomplete; context changes what it means",
  pr: "marketing framing; not false, not load-bearing",
  bs: "false, unsourced, or contradicted by evidence",
  unv: "could not be checked in this research",
};
const TAG_ORDER = ["signal", "nuance", "pr", "bs", "unv"];

function Tag({ t }) {
  return <span className={"dr-tag dr-tag-" + t}>{TAGS[t]}</span>;
}

function Conf({ c }) {
  if (!c || c === "n/a") return null;
  return <span className="dr-conf">{c}</span>;
}

function RatioBar({ lines }) {
  const n = lines.length;
  return (
    <div>
      <div className="dr-ratio">
        {TAG_ORDER.map((t) => {
          const c = lines.filter((l) => l.tag === t).length;
          if (!c) return null;
          return (
            <span
              key={t}
              className={"dr-ratio-seg dr-tag-bg-" + t}
              style={{ width: (100 * c) / n + "%" }}
              title={`${TAGS[t]} ${c}`}
            />
          );
        })}
      </div>
      <div className="dr-ratio-key">
        {TAG_ORDER.map((t) => {
          const c = lines.filter((l) => l.tag === t).length;
          return (
            <span key={t}>
              <i className={"dr-dot dr-tag-bg-" + t} />
              {TAGS[t]} {c}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function LinesBlock({ lines, filter, onFilter, depth }) {
  const counts = {};
  lines.forEach((l) => (counts[l.tag] = (counts[l.tag] || 0) + 1));
  const visible = lines.filter((l) => filter === "all" || l.tag === filter);
  return (
    <div>
      <div className="dr-filters">
        <button className={filter === "all" ? "on" : ""} onClick={() => onFilter("all")}>
          All {lines.length}
        </button>
        {TAG_ORDER.map((t) => (
          <button key={t} className={filter === t ? "on" : ""} onClick={() => onFilter(t)}>
            {TAGS[t]} {counts[t] || 0}
          </button>
        ))}
      </div>
      <div className="dr-lines">
        {visible.map((l) => (
          <div key={l.id} className="dr-line" id={l.id}>
            <div className="dr-line-idx">{l.id}</div>
            <div>
              <p className="dr-line-q">{l.q}</p>
              <div className="dr-line-head">
                <Tag t={l.tag} />
                <span className="dr-line-short">{l.short}</span>
                <Conf c={l.conf} />
              </div>
              {depth >= 2 && (
                <div className="dr-line-why" dangerouslySetInnerHTML={{ __html: l.why }} />
              )}
              {depth >= 3 && (
                <div className="dr-line-ev">
                  <span className="dr-eyebrow">Evidence and confidence</span>
                  <ul>
                    {l.ev.map((e, i) => (
                      <li key={i} dangerouslySetInnerHTML={{ __html: e }} />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CLAIM_LABEL = { ok: "Confirmed", mid: "Partly", bad: "Contradicted", unk: "Unverified" };
const CLAIM_COLOR = { ok: "#1e7d3a", mid: "#a8720f", bad: "#b3372f", unk: "#6b7280" };

function ClaimsTable({ claims }) {
  return (
    <div className="dr-scroll">
      <table className="dr-tbl">
        <thead>
          <tr>
            <th style={{ width: "34%" }}>Claim in the document</th>
            <th style={{ width: "11%" }}>Sources say</th>
            <th>What the evidence says</th>
          </tr>
        </thead>
        <tbody>
          {claims.map((c, i) => (
            <tr key={i}>
              <td>{c.c}</td>
              <td style={{ color: CLAIM_COLOR[c.v], fontWeight: 600 }}>{CLAIM_LABEL[c.v]}</td>
              <td>{c.n}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ROLE_LABEL = {
  inc: "Direct competitor / incumbent",
  adj: "Big company that could move in",
  st: "Startup",
  pre: "Precedent or channel",
  gone: "Failed, exited or discontinued",
};
const ROLE_COLOR = { inc: "#b3372f", adj: "#a8720f", st: "#2a5fb0", pre: "#1e7d3a", gone: "#6b7280" };

function Landscape({ items }) {
  return (
    <div className="dr-land">
      {items.map((c, i) => (
        <div className="dr-land-card" key={i}>
          <div className="dr-land-name">{c.nm}</div>
          <div className="dr-land-role" style={{ borderColor: ROLE_COLOR[c.role] }}>
            {ROLE_LABEL[c.role]}
          </div>
          <p>{c.p}</p>
          <div className="dr-land-src">{c.src}</div>
        </div>
      ))}
    </div>
  );
}

const UC_LABEL = { ok: "Yes", mid: "Partly", bad: "No", unk: "Unknown" };
const UC_COLOR = { ok: "#1e7d3a", mid: "#a8720f", bad: "#b3372f", unk: "#6b7280" };

function UcCell({ x }) {
  return <td style={{ color: UC_COLOR[x[0]], fontWeight: 500 }}>{x[1]}</td>;
}

function UcMatrix({ rows }) {
  return (
    <div className="dr-scroll">
      <table className="dr-tbl">
        <thead>
          <tr>
            <th>Use case</th>
            <th>Legal basis (Germany unless stated)</th>
            <th>Identifiable buyer</th>
            <th>Legal duty forcing purchase</th>
            <th>EU AI Act exposure</th>
            <th>Incumbent in the seat</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>
                <strong>{r.u}</strong>
              </td>
              <UcCell x={r.legal} />
              <UcCell x={r.buyer} />
              <UcCell x={r.duty} />
              <UcCell x={r.ai} />
              <UcCell x={r.inc} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Fig({ title, sub, note, children }) {
  return (
    <div className="dr-fig">
      <div className="dr-fig-title">{title}</div>
      <div className="dr-fig-sub">{sub}</div>
      {children}
      {note && <div className="dr-fig-note">{note}</div>}
    </div>
  );
}

function HBars({ items, opts }) {
  const W = opts.w || 640,
    rowH = 26,
    pad = { l: opts.l || 220, r: 70, t: 6, b: 6 };
  const H = pad.t + pad.b + items.length * rowH;
  const max = opts.max || Math.max(...items.map((i) => Math.abs(i.v)));
  const min = opts.min || 0;
  const x = (v) => pad.l + ((v - min) / (max - min)) * (W - pad.l - pad.r);
  const ticks = opts.ticks || [];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={opts.label || ""} className="dr-chart">
      {ticks.map((t) => (
        <line key={"g" + t} className="dr-grid" x1={x(t)} x2={x(t)} y1={pad.t} y2={H - pad.b} />
      ))}
      {items.map((it) => {
        const y = pad.t + items.indexOf(it) * rowH + 4,
          h = rowH - 8,
          v = it.v;
        const x0 = x(Math.min(0, v)),
          x1 = x(Math.max(0, v));
        const fill = it.fill || opts.fill || "#242424";
        const label = opts.fmt ? opts.fmt(v, it) : v;
        return (
          <React.Fragment key={it.n}>
            <text x={pad.l - 8} y={y + h / 2 + 4} textAnchor="end">
              {it.n}
            </text>
            <rect x={x0} y={y} width={Math.max(1, x1 - x0)} height={h} fill={fill}>
              <title>
                {it.n}: {label}
              </title>
            </rect>
            <text
              className="dr-chart-value"
              x={(v >= 0 ? x1 : x0) + (v >= 0 ? 5 : -5)}
              y={y + h / 2 + 4}
              textAnchor={v >= 0 ? "start" : "end"}
            >
              {label}
            </text>
          </React.Fragment>
        );
      })}
      {(opts.refs || []).map((r) => (
        <React.Fragment key={r.n}>
          <line x1={x(r.v)} x2={x(r.v)} y1={pad.t} y2={H - pad.b} stroke="#9e2a2b" strokeDasharray="4 3" />
          <text x={x(r.v) + 4} y={pad.t + 9} fill="#9e2a2b">
            {r.n}
          </text>
        </React.Fragment>
      ))}
      {ticks.map((t) => (
        <text key={"tl" + t} x={x(t)} y={H + 12} textAnchor="middle" fill="#777">
          {opts.tickFmt ? opts.tickFmt(t) : t}
        </text>
      ))}
    </svg>
  );
}

function RangeBars({ items, opts }) {
  const W = 640,
    rowH = 26,
    pad = { l: 190, r: 50, t: 6, b: 6 };
  const H = pad.t + pad.b + items.length * rowH;
  const max = opts.max;
  const x = (v) => pad.l + (v / max) * (W - pad.l - pad.r);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={opts.label} className="dr-chart">
      {[0, 25, 50, 75, 100].map((t) => (
        <React.Fragment key={t}>
          <line className="dr-grid" x1={x(t)} x2={x(t)} y1={pad.t} y2={H - pad.b} />
          <text x={x(t)} y={H + 12} textAnchor="middle" fill="#777">
            €{t}
          </text>
        </React.Fragment>
      ))}
      {items.map((it) => {
        const y = pad.t + items.indexOf(it) * rowH + 4,
          h = rowH - 8;
        return (
          <React.Fragment key={it.n}>
            <text x={pad.l - 8} y={y + h / 2 + 4} textAnchor="end">
              {it.n}
            </text>
            <rect x={x(it.lo)} y={y} width={Math.max(4, x(it.hi) - x(it.lo))} height={h} fill="#242424">
              <title>
                {it.n}: €{it.lo} to €{it.hi}
              </title>
            </rect>
            <text className="dr-chart-value" x={x(it.hi) + 5} y={y + h / 2 + 4}>
              €{it.lo}
              {it.hi !== it.lo ? ` to €${it.hi}` : ""}
            </text>
          </React.Fragment>
        );
      })}
      <line x1={x(55)} x2={x(55)} y1={pad.t} y2={H - pad.b} stroke="#9e2a2b" strokeDasharray="4 3" />
      <text x={x(55) + 4} y={pad.t + 9} fill="#9e2a2b">
        €55 consent cap (§56 OWiG)
      </text>
    </svg>
  );
}

function ReportBody() {
  const [depth, setDepth] = useState(2);
  const [filterA, setFilterA] = useState("all");
  const [filterB, setFilterB] = useState("all");

  useEffect(() => {
    try {
      const saved = localStorage.getItem("danielReportDepth");
      if (saved) setDepth(Number(saved));
    } catch (e) {}
  }, []);

  const changeDepth = (d) => {
    setDepth(d);
    try {
      localStorage.setItem("danielReportDepth", String(d));
    } catch (e) {}
  };

  return (
    <div className="dr-page">
      <Helmet>
        <title>Intelligent Collateral and IS4U | Helia Jamshidi</title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
      </Helmet>
      <div className="ai-report-banner">
        <strong>AI-researched report</strong>
        <span>Worth sharing, with that caveat.</span>
      </div>
      <h1>Intelligent Collateral and IS4U</h1>

      <div className="dr-controls">
        <div className="dr-control-group">
          <span className="dr-control-label">Depth</span>
          <div className="dr-toggle">
            <button className={depth === 1 ? "on" : ""} onClick={() => changeDepth(1)}>
              Big picture
            </button>
            <button className={depth === 2 ? "on" : ""} onClick={() => changeDepth(2)}>
              Detail
            </button>
            <button className={depth === 3 ? "on" : ""} onClick={() => changeDepth(3)}>
              Full depth + sources
            </button>
          </div>
        </div>
      </div>

      <section className="dr-section" id="howto">
        <p className="dr-lede">
          Every sentence of both documents is tagged below. Use the depth control above to add the
          explanation behind each line, then the evidence and sources behind that, each marked HIGH,
          MEDIUM, LOW or UNVERIFIED for how far it could be checked.
        </p>
        <div className="dr-tag-legend">
          {TAG_ORDER.map((t) => (
            <div key={t}>
              <Tag t={t} />
              <span>{TAGDESC[t]}</span>
            </div>
          ))}
        </div>
      </section>

      <hr className="dr-sep" />

      <div className="proj-a dr-project">
        <section className="dr-section" id="a-lines">
          <span className="dr-eyebrow">Project A · Intelligent Collateral</span>
          <h2>Line by line</h2>
          <p className="dr-lede">
            Every sentence of the one-pager, in order, with a tag and a translation. Filter by tag.
            Switch depth for the reasoning and sources.
          </p>
          <LinesBlock lines={A.lines} filter={filterA} onFilter={setFilterA} depth={depth} />
        </section>

        <section className="dr-section" id="a-claims">
          <span className="dr-eyebrow">Project A</span>
          <h2>Claims checked</h2>
          <ClaimsTable claims={A.claims} />
          <Fig
            title="Market size: the headline versus the slice you can sell into"
            sub="US dollars, billions. Each step is a verified figure or a bounded estimate."
            note="Sources: ELFA 2026 fact sheet ($2.3T, 58% financed), BEA via FRED (software $645B of the base), Cat Financial and Deere 10-Ks (~10% residual-exposed), UK FLA (~24% operating leases). Last bar is the midpoint of a $81B to $203B range. ELFA's own survey (SEFA) holds the exact split, behind a paywall."
          >
            <HBars
              items={A.tam}
              opts={{
                max: 2400,
                fmt: (v) => "$" + v + "B",
                ticks: [0, 500, 1000, 1500, 2000],
                tickFmt: (t) => "$" + t + "B",
                l: 300,
                label: "Market size funnel",
              }}
            />
          </Fig>
        </section>

        <section className="dr-section" id="a-customers">
          <span className="dr-eyebrow">Project A</span>
          <h2>Customers and the sales cycle</h2>
          <div className="dr-grid3">
            <div className="dr-mini">
              <span className="dr-eyebrow">Who signs</span>
              <div className="dr-big">SVP, Head of Equipment Management</div>
              <div className="dr-sub">
                or VP Asset Management. Verified titles at BofA, Wells Fargo, U.S. Bank, PNC, Truist,
                M&amp;T, Fifth Third, Huntington, DLL. Not the CRO.
              </div>
            </div>
            <div className="dr-mini">
              <span className="dr-eyebrow">The whole room</span>
              <div className="dr-big">378</div>
              <div className="dr-sub">
                attendees at ELFA's 2025 Equipment Management Conference, an all-time high. Booth about
                $3,000 to $4,000. Cheap to reach, few logos.
              </div>
            </div>
            <div className="dr-mini">
              <span className="dr-eyebrow">Model validation line</span>
              <div className="dr-big">$30B</div>
              <div className="dr-sub">
                Banks above this in assets must validate a vendor model before first use (SR 26-2).
                Below it: independents and regional lessors, and independents grew 17.7% in 2024 while
                banks shrank.
              </div>
            </div>
          </div>
          <h3>What a first deal has to pass through</h3>
          <div className="dr-flow">
            <div className="dr-st">
              <span className="dr-eyebrow">1</span>
              <b>Meet the buyer</b>ELFA Equipment Management Conference (Feb) or Annual Convention
              (Oct). Account-based, seventeen named people.
            </div>
            <div className="dr-st">
              <span className="dr-eyebrow">2</span>
              <b>Backtest on their book</b>Booked residual vs realized proceeds from their disposition
              history. This is where "80% accuracy" gets tested.
              <div className="dr-gate">Gate: data-sharing agreement</div>
            </div>
            <div className="dr-st">
              <span className="dr-eyebrow">3</span>
              <b>Third-party risk due diligence</b>Audited financials, SEC filings if any, "depth of
              resources (including staffing)", SOC 2, pen tests (2023 interagency guidance).
              <div className="dr-gate">Gate: a seed-stage company usually fails "access to funds"</div>
            </div>
            <div className="dr-st">
              <span className="dr-eyebrow">4</span>
              <b>Model validation</b>Conceptual soundness, development data, performance, ongoing
              monitoring. Required before first use at banks over $30B.
              <div className="dr-gate">Gate: explainability is mandatory</div>
            </div>
            <div className="dr-st">
              <span className="dr-eyebrow">5</span>
              <b>Land in the annual review</b>The product must feed the "annual portfolio residual
              adequacy review" and the ABL appraisal reset, both annual by contract.
            </div>
          </div>
          {depth >= 2 && (
            <div className="dr-callout warn">
              <span className="dr-eyebrow">Duration</span>
              <p>
                No published statistic on bank vendor onboarding time survived verification, so no
                number is given here. The structure is two serial gates before the first dollar (due
                diligence before contract, validation before use), and neither is under the vendor's
                control. Ask the founder for the longest and shortest completed deals.
              </p>
            </div>
          )}
          {depth >= 2 && (
            <>
              <h3>Who is a customer and who is not</h3>
              <ul className="dr-tight">
                <li>
                  <b>Yes:</b> bank equipment finance arms and independents with operating-lease books;
                  OEM captives only where dealer guarantees do not already absorb the risk.
                </li>
                <li>
                  <b>Maybe:</b> ABS investors (S&amp;P and KBRA take the lessor's booked residual with no
                  third-party check, a real gap, but agencies are not buyers); residual value insurers
                  and guarantors, who now hold the risk and have no independent mark.
                </li>
                <li>
                  <b>No:</b> regulators (they read the bank's documents), remarketing platforms and OEMs
                  (they own the data and are the competition), family offices (no evidence of demand).
                </li>
              </ul>
            </>
          )}
        </section>

        <section className="dr-section" id="a-ground">
          <span className="dr-eyebrow">Project A</span>
          <h2>Reality on the ground</h2>
          <div className="dr-grid2">
            <div>
              <h4>The ratchet</h4>
              <p>
                "Estimated residual values should not be revised upward" (OCC). Every accuracy
                improvement that says "worth less" creates an immediate charge for the customer; every
                one that says "worth more" is unusable in the accounts. The product's output is
                structurally bad news for the accountants and good news only for pricing and remarketing
                teams.
              </p>
              <h4>Annual is written into contracts</h4>
              <p>
                Asset-based loans reset equipment appraisals once per 12 months and amortize on a
                7-year straight line in between; extra appraisals trigger only in distress. Selling
                "continuous" into a process that contractually resets once a year means changing the
                contract, not just the software.
              </p>
              {depth >= 2 && (
                <>
                  <h4>The pain is real but already hedged</h4>
                  <p>
                    Deere: a 10 percent market drop with every unit returned costs about $65 million on
                    a $7.6 billion book, after dealer guarantees. Wells Fargo "typically" buys residual
                    value insurance; PNC insures $0.6 billion of residuals. Several banks buy down the
                    risk rather than measure it better.
                  </p>
                  <h4>Nobody is being forced</h4>
                  <p>
                    The OCC's Spring 2026 and Fall 2025 risk reports do not contain the word "residual".
                    Delinquencies are 1.7 percent and charge-offs 0.54 percent. A chief risk officer
                    will not feel urgency. The honest framing is an earnings and depreciation problem,
                    not a credit problem.
                  </p>
                </>
              )}
            </div>
            <div>
              <Fig
                title="Ryder: used vehicle proceeds per unit, year over year"
                sub="Percent change, constant currency. The two years in which Ryder did not adjust booked residuals."
                note="Ryder FY2025 10-K (HIGH). Gains on vehicle sales fell from $216M (2023) to $103M (2024) to $69M (2025). A 10% cut in residuals would add about $340M of depreciation. PwC flagged residual values as a critical audit matter."
              >
                <HBars
                  items={A.ryder}
                  opts={{
                    min: -30,
                    max: 5,
                    fmt: (v) => v + "%",
                    ticks: [-30, -20, -10, 0],
                    tickFmt: (t) => t + "%",
                    l: 120,
                    fill: "#9e2a2b",
                    label: "Ryder used vehicle proceeds",
                  }}
                />
              </Fig>
              {depth >= 2 && (
                <div className="dr-callout good">
                  <span className="dr-eyebrow">Best evidence for the pitch</span>
                  <p>
                    Ryder went two full years without adjusting residuals during the worst used-truck
                    downturn in a decade, and its auditor's test of that assumption relies on
                    "third-party expected future market prices" that no vendor refreshes continuously.
                    That is the product's job description, written by PwC.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="dr-section" id="a-cold">
          <span className="dr-eyebrow">Project A</span>
          <h2>Cold start: how this would have to begin</h2>
          <ol className="dr-steps">
            <li>
              <b>Get data you are allowed to use.</b> Auction data is owned by RB Global (which owns
              Rouse) and will not be licensed to a competitor. Sandhills indices are free but aggregate.
              EquipmentWatch and Black Book sell APIs whose terms likely forbid derivative products
              (unverified). The realistic training set is a lessor's own history: booked residual,
              realized proceeds, contract terms, per unit. That means the first customer is also the
              data supplier, under a data-sharing agreement.
            </li>
            <li>
              <b>Pick one asset class and one segment.</b> Construction and trucks are Rouse's home
              turf. Ag is thin. Medical, machine tools and IT are essentially unserved and have no
              Rouse. Under $30 billion in assets there is no formal model validation, and independents
              are growing.
            </li>
            <li>
              <b>Ship a report before a feed.</b> The recurring artifact in the buyer's life is the
              annual residual adequacy review. A "review pack" that arrives quarterly, with factor
              attribution and a backtest against their own realizations, fits the workflow. A continuous
              API does not, yet.
            </li>
            <li>
              <b>Prepare the validation package as a product.</b> Conceptual soundness write-up,
              development data description, backtesting evidence, ongoing monitoring reports. Without
              it no bank can buy.
            </li>
            <li>
              <b>Prepare the corporate hygiene banks ask for.</b> Audited financials, SOC 2, penetration
              test results, key-person and continuity plans. The 2023 interagency guidance asks for all
              of it before contract.
            </li>
            <li>
              <b>Distribution through the lease platforms.</b> Odessa, LTi, Alfa, Leasepath and
              Northteq hold the asset record and none values it. An embedded valuation feed is the
              scalable channel, once there is a reference customer.
            </li>
          </ol>
          {depth >= 2 && (
            <div className="dr-callout">
              <span className="dr-eyebrow">Time to scale, from the analogues</span>
              <p>
                Rouse took two decades to become a $251.7 million exit. Tamarack partnered with Sandhills
                on "AI and market asset values for portfolio management" in April 2023 and was acquired
                by Liventus in April 2026 without visibly solving this. Be ready for the question of why
                this is not the fourth attempt.
              </p>
            </div>
          )}
        </section>

        <section className="dr-section" id="a-land">
          <span className="dr-eyebrow">Project A</span>
          <h2>Players and competitors</h2>
          <p className="dr-lede">
            Ranked threats: RB Global/Rouse, Moody's, Randall-Reilly/Fusable. Wildcard: Samsara. Most
            underrated: residual value insurers.
          </p>
          <Landscape items={A.landscape} />
          <Fig
            title="What this category has sold for"
            sub="US dollars, millions. Exits and outcomes of equipment data and marketplace businesses."
            note="IronPlanet: Ritchie Bros 8-K 2016 (HIGH). Rouse: Ritchie Bros FY2020 10-K, $251.7M net (HIGH). SmartEquip: ~$175M to $217M, sources differ (MEDIUM). Machinio: Liquidity Services 10-K (HIGH). Barker: Business Wire (MEDIUM). Dozr: BetaKit (HIGH). The realistic acquirer is a data or auction incumbent, at data-business multiples."
          >
            <HBars
              items={A.exits}
              opts={{
                max: 800,
                fmt: (v, it) => (it.v === 0 ? "receivership" : "$" + v + "M"),
                ticks: [0, 200, 400, 600, 800],
                tickFmt: (t) => "$" + t + "M",
                l: 170,
                label: "Exit values",
              }}
            />
          </Fig>
        </section>

        <section className="dr-section" id="a-other">
          <span className="dr-eyebrow">Project A</span>
          <h2>Other facts that matter</h2>
          <ul className="dr-tight">
            <li>
              <b>The patent.</b> US 2004/0236587 A1, "Lifecycle profitability tool for leasable assets",
              inventor Dan Nalawade, assignee General Electric, filed May 2003, abandoned. A lease
              pricing calculator. If the founder is the inventor, they worked at GE Capital in 2003,
              which is relevant experience, but the sentence in the one-pager is not accurate and an
              investor will check.
            </li>
            <li>
              <b>The name.</b> asariri.ai is a live, unrelated voice-AI company. No SEC trace of an
              equipment-finance Asariri.
            </li>
            <li>
              <b>The rulebook changed in April 2026.</b> SR 11-7 and OCC 2011-12 are gone, replaced by
              SR 26-2 with the $30 billion threshold. The OCC's lease handbook still points at the
              rescinded bulletins. Anyone pitching SR 11-7 compliance is out of date; anyone who knows
              this has a conversation opener.
            </li>
            <li>
              <b>Basel gives nothing.</b> Lease residuals carry a flat 100 percent risk weight; the US
              rescinded the proposal that would have recognized physical collateral. Any "capital
              efficiency" slide is wrong.
            </li>
            <li>
              <b>CECL is the one accounting hook.</b> Fair value of collateral is required for
              collateral-dependent loans, but the appraisal regime it references is real-estate only, so
              equipment fair value stays a management estimate. That is an opening, not a mandate.
            </li>
            <li>
              <b>Europe is smaller than it looks.</b> Leaseurope: EUR 454 billion of new leasing in
              2024, of which 75 percent is cars. The equipment slice is about EUR 100 billion.
            </li>
            <li>
              <b>Buy the SEFA report.</b> ELFA's annual survey has tables on residual position by
              equipment type and on why FMV leases terminate the way they do. Two pages answer whether
              this business can exist.
            </li>
            {depth >= 2 && (
              <li>
                <b>Antitrust footnote.</b> Rouse's rental benchmarking has drawn comparisons to the Agri
                Stats and RealPage cases. If RB Global ever has to open its data, that changes the
                picture for a challenger.
              </li>
            )}
          </ul>
        </section>
      </div>

      <hr className="dr-sep" />

      <div className="proj-b dr-project">
        <section className="dr-section" id="b-lines">
          <span className="dr-eyebrow">Project B · IS4U</span>
          <h2>Line by line</h2>
          <p className="dr-lede">
            Every sentence of the two sections you were given, in order. The document is a fragment;
            the sections it promises (economics, rollout, competition, risks) were not included.
          </p>
          <LinesBlock lines={B.lines} filter={filterB} onFilter={setFilterB} depth={depth} />
        </section>

        <section className="dr-section" id="b-claims">
          <span className="dr-eyebrow">Project B</span>
          <h2>Claims checked</h2>
          <ClaimsTable claims={B.claims} />
          <h3>The five use cases, scored</h3>
          <UcMatrix rows={B.usecases} />
          {depth >= 2 && (
            <p className="dr-fig-note">
              Legal columns reflect German law as of September 2026 unless the row says otherwise. AI
              Act high-risk duties are deferred to 2 December 2027 (Digital Omnibus, MEDIUM);
              prohibitions have applied since February 2025 (HIGH).
            </p>
          )}
        </section>

        <section className="dr-section" id="b-customers">
          <span className="dr-eyebrow">Project B</span>
          <h2>Customers and the sales cycle</h2>
          <div className="dr-grid3">
            <div className="dr-mini">
              <span className="dr-eyebrow">Buyers per city</span>
              <div className="dr-big">3 to 5</div>
              <div className="dr-sub">
                Ordnungsamt (parking), Tiefbauamt (roads), transit operator (a separate company),
                police, planning. Separate budgets, separate procurements, separate data-protection
                officers.
              </div>
            </div>
            <div className="dr-mini">
              <span className="dr-eyebrow">EU tender thresholds 2026-27</span>
              <div className="dr-big">€216k / €432k</div>
              <div className="dr-sub">
                City department / transit utility, supplies and services. Above these: EU-wide tender,
                120-day benchmark to award, 10 to 15 day standstill, appeals.
              </div>
            </div>
            <div className="dr-mini">
              <span className="dr-eyebrow">Time to scale, two analogues</span>
              <div className="dr-big">~7 years</div>
              <div className="dr-sub">
                vialytics to 1,000 communities; Hayden AI to 10 major US cities. Hayden's first big
                award took 21 months from test to contract, non-competitively.
              </div>
            </div>
          </div>
          <Fig
            title="Germany: how much a city can buy without any procedure"
            sub="Direct-award limit for supplies and services, EUR, by Land. Bavaria's rule sunsets end of 2029."
            note="Source: dtvp.de Vergabelexikon, single secondary source; Bavaria corroborated by a second source. Berlin appears to be the unraised EUR 1,000 baseline of §14 UVgO. Verify with each Land ministry before planning. Federal level: EUR 50,000 from 1 July 2026."
          >
            <HBars
              items={B.direkt}
              opts={{
                max: 230000,
                fmt: (v, it) => "€" + v.toLocaleString("en") + " (" + it.c + ")",
                ticks: [0, 50000, 100000, 150000, 200000],
                tickFmt: (t) => "€" + t / 1000 + "k",
                l: 280,
                refs: [
                  { v: 216000, n: "EU threshold €216k" },
                  { v: 40000, n: "typical vialytics contract ~€40k" },
                ],
                label: "Direct award limits by Land",
              }}
            />
          </Fig>
          <h3>What a first deal has to pass through</h3>
          <div className="dr-flow">
            <div className="dr-st">
              <span className="dr-eyebrow">1</span>
              <b>Find the department with a duty</b>Only road condition has a legal obligation
              (Verkehrssicherungspflicht, register updates every four years) that forces the purchase.
            </div>
            <div className="dr-st">
              <span className="dr-eyebrow">2</span>
              <b>Get onto a vehicle</b>Bus: separate company, works council co-determination (§87
              BetrVG), DPIA, signage, operator becomes controller. Garbage truck: city-owned, every
              street weekly.
              <div className="dr-gate">Gate: Betriebsvereinbarung</div>
            </div>
            <div className="dr-st">
              <span className="dr-eyebrow">3</span>
              <b>Price under the limit</b>Below the Land's direct-award limit, or use the §8(4) UVgO
              "cannot be specified for comparable bids" ground to negotiate. Above EUR 216,000: EU
              tender.
              <div className="dr-gate">Gate: a budget line must exist this year</div>
            </div>
            <div className="dr-st">
              <span className="dr-eyebrow">4</span>
              <b>Data protection sign-off</b>Lawful basis, necessity and proportionality under §4 BDSG,
              DPIA, deletion schedule. For enforcement in Germany: not available to a private vendor.
              <div className="dr-gate">Gate: §63g, OLG Frankfurt</div>
            </div>
            <div className="dr-st">
              <span className="dr-eyebrow">5</span>
              <b>Renew</b>vialytics claims 95% retention on a duty-driven product. Nothing forces renewal
              of enforcement, incident or progress products.
            </div>
          </div>
          {depth >= 2 && (
            <div className="dr-callout warn">
              <span className="dr-eyebrow">Pilots</span>
              <p>
                No verified statistic on smart-city pilot-to-contract conversion exists; do not accept
                one without a source. Germany's Modellprojekte Smart Cities programme (EUR 820 million,
                73 projects) is grant money on a fixed timeline, the textbook setup for a pilot that ends
                with the grant. A free pilot creates no budget line, which is the thing you actually
                need.
              </p>
            </div>
          )}
        </section>

        <section className="dr-section" id="b-ground">
          <span className="dr-eyebrow">Project B</span>
          <h2>Reality on the ground</h2>
          <div className="dr-grid2">
            <div>
              <h4>Germany decided this in July 2026</h4>
              <p>
                §63g StVG is the first legal basis for scan-vehicle parking control. It is available
                only to "die zuständigen Behörden", forbids covert operation, requires the vehicles to
                be visibly marked, mandates deletion within 24 hours, generally requires a second
                confirming observation, and in paragraph 6 declares processing the data for other
                purposes unlawful. The Bundesrat asked for crime-prevention reuse and was refused.
              </p>
              <h4>Private enforcement is unlawful</h4>
              <p>
                A line of OLG Frankfurt decisions (2017 to 2020) holds that delegating traffic
                enforcement to private contractors is unlawful and the evidence inadmissible, extended
                to parking in January 2020. Cities responded by hiring civil servants. There is no
                German equivalent of Verra or Egis to partner with.
              </p>
              {depth >= 2 && (
                <>
                  <h4>Moving violations need a face</h4>
                  <p>
                    §25a StVG lets the authority charge the vehicle owner only the procedural costs, and
                    only for stopping and parking. A bus-lane violation from a moving bus requires
                    identifying the driver. That is why Frankfurt's buses have a button, not an AI.
                  </p>
                  <h4>The customer is broke</h4>
                  <p>
                    Deutscher Städtetag, June 2026: municipal deficits of EUR 29.7bn in 2026, about EUR
                    120bn cumulative to 2029, reserves exhausted after three years. Over 1,500
                    municipalities held an action day. The first item on their list of cuts is "weniger
                    Busse und Bahnen".
                  </p>
                </>
              )}
            </div>
            <div>
              <Fig
                title="German parking fines versus the consent cap"
                sub="Bußgeldkatalog 2025, EUR. Everything at or below EUR 55 can be a consented warning; above it needs a full proceeding with the driver identified."
                note="bussgeldkatalog.org (MEDIUM); §56 OWiG (HIGH). Compare New York: $50 to $250 escalating, keeper liable, 1.7 million tickets in two years worth $225 million."
              >
                <RangeBars items={B.fines} opts={{ max: 120, label: "German parking fines" }} />
              </Fig>
              <Fig
                title="Flagship DACH bus fleets"
                sub="Number of buses. The entire top-of-funnel hardware volume before the long tail."
                note="de.wikipedia operator articles (MEDIUM). MVG runs a further 238 partner buses. About 3,600 buses across all four; at Hayden's ~$20,000 per system per year that is a ceiling of ~$72M per year if every bus in every flagship city were fitted, which none of them can legally do for enforcement."
              >
                <HBars
                  items={B.fleets}
                  opts={{
                    max: 1800,
                    fmt: (v) => v.toLocaleString("en"),
                    ticks: [0, 500, 1000, 1500],
                    l: 170,
                    label: "Bus fleet sizes",
                  }}
                />
              </Fig>
            </div>
          </div>
          {depth >= 2 && (
            <div className="dr-callout">
              <span className="dr-eyebrow">What the legal wall looks like elsewhere</span>
              <p>
                <b>Netherlands:</b> parking is a tax (Art. 225 Gemeentewet), owner liable, scan cars
                lawful; the regulator now reports about 500,000 wrongful fines a year. <b>France:</b>{" "}
                decriminalized in 2018, contractors issue the FPS with about 25 percent revenue share;
                Streeteo executives were tried in 2023 for fabricated records. <b>UK:</b> civil
                enforcement, keeper liable, vehicle-mounted devices need VCA certification; Hayden holds
                it, no council has declared use. <b>US:</b> the market that works, on fixed fees, with
                accuracy scandals (New York) and bans (Texas red-light cameras, Ontario speed cameras).
              </p>
            </div>
          )}
        </section>

        <section className="dr-section" id="b-cold">
          <span className="dr-eyebrow">Project B</span>
          <h2>Cold start: how this would have to begin</h2>
          <ol className="dr-steps">
            <li>
              <b>Cut four of the five use cases.</b> In Germany, enforcement is closed to private
              vendors, incident detection has no lawful basis and no buyer, traffic flow is already
              served by GPS data, and construction progress has no municipal buyer anywhere. Road
              condition is the only one with a legal duty behind it.
            </li>
            <li>
              <b>Change the vehicle.</b> A garbage truck is city-owned, visits every street weekly, and
              does not trigger a transit operator's board, procurement and works council. That is
              vialytics' choice and it is the right one.
            </li>
            <li>
              <b>Pick the Land by its direct-award limit.</b> Bavaria allows EUR 100,000 without a
              procedure until end of 2029; Berlin allows about EUR 1,000 and is twelve districts. Price
              a town of 400 km at about EUR 40,000 and it never sees a tender.
            </li>
            <li>
              <b>Sell against the duty, never against ZEB.</b> Visual municipal inspection under the
              Verkehrssicherungspflicht is a recognized method. ZEB conformity needs a BASt-certified
              vehicle with laser profilometry and a skid-resistance wheel; a dashcam cannot get there.
            </li>
            <li>
              <b>Then face the fact that this is vialytics.</b> 1,000+ communities claimed, EUR 100 per
              km, 95 percent retention claimed, seven years of head start, plus RoadAI inside Vaisala
              and Waymo giving potholes away free. A late entrant needs something they lack: a better
              price ladder, a different buyer (Landkreise and state road authorities, which is Blyncsy's
              answer), or a different sensor.
            </li>
            <li>
              <b>If enforcement is the goal, leave Germany.</b> The UK route is defined (VCA device
              approval, keeper liability, councils with moving-traffic powers) and no council has yet
              declared a bus-mounted device. Partner with the enforcement incumbent, as Hayden did with
              Conduent and Verra, because the incumbent owns the ticket back office.
            </li>
            <li>
              <b>Delete the robotaxi slide</b> until one operator anywhere has paid or been paid for
              perception data by a city.
            </li>
          </ol>
        </section>

        <section className="dr-section" id="b-land">
          <span className="dr-eyebrow">Project B</span>
          <h2>Players and competitors</h2>
          <p className="dr-lede">
            Every viable seat is occupied: enforcement by Hayden and Seon on fixed fees behind Verra and
            Modaxo back offices; road condition by vialytics, RoadAI and Blyncsy; planning data by
            Geotab. The graveyard is large and recent.
          </p>
          <Landscape items={B.landscape} />
          {depth >= 2 && (
            <div className="dr-callout">
              <span className="dr-eyebrow">Big companies that could move in, and what was found</span>
              <p>
                Geotab already has. Bosch added NIRA Dynamics' road-surface alerts to its connected-vehicle
                services rather than building a municipal product, so road data increasingly arrives free
                from cars people already own. Bentley bought Blyncsy. Michelin bought RoadBotics and
                quit. Nothing could be verified for Continental, Motorola Solutions, Axon (beyond Denver
                replacing Flock with Axon), Trimble, Hexagon, Esri, Lytx, Netradyne, Motive or Nvidia; do
                not assume their absence.
              </p>
            </div>
          )}
        </section>

        <section className="dr-section" id="b-other">
          <span className="dr-eyebrow">Project B</span>
          <h2>Other facts that matter</h2>
          <ul className="dr-tight">
            <li>
              <b>Hayden AI is not a revenue-share business.</b> $19,867 per system per year at the MTA,
              fixed. Anyone modelling a share of fines for Germany is modelling something that is
              illegal there and not even how the US leader works.
            </li>
            <li>
              <b>The margins are hardware-services margins.</b> Verra's enforcement segment runs at
              about 26 percent versus 65 percent for its commercial business; Conduent's enforcement
              unit sold for 1.7 times revenue.
            </li>
            <li>
              <b>Accuracy is the operational risk that becomes political risk.</b> Netherlands 1 in 10
              wrongful; New York "thousands of mistaken" tickets; Roseville CA audit of Flock found 71
              percent of alerts were wrong plates. Ask for a measured false-positive rate on a held-out
              set, in writing.
            </li>
            <li>
              <b>Secondary use is the political killer.</b> Flock's collapse in 2026 was triggered by
              data collected for one purpose being queried for another. "Eyes of the municipality" is
              that architecture by design.
            </li>
            <li>
              <b>Works councils have a statutory veto point.</b> §87(1)(6) BetrVG makes camera systems
              that could monitor drivers subject to co-determination. Budget it as a timeline item, not
              a formality.
            </li>
            <li>
              <b>The AI Act clock.</b> Prohibitions since February 2025; Annex III high-risk duties
              deferred to December 2027 by the Digital Omnibus (regulation number reported as
              2026/1744, MEDIUM). Design so that no person is ever identified and say so in writing.
            </li>
            {depth >= 2 && (
              <>
                <li>
                  <b>Wejo is the cautionary tale for "data layer" businesses.</b> $450 million raised, 42
                  million connected cars, $8.4 million of revenue, administration in 2023. Supply was
                  never the bottleneck.
                </li>
                <li>
                  <b>Unverified but important.</b> Who owns the buses under contracted operation and
                  what happens to retrofit hardware at re-tender; whether windscreen mounting affects a
                  German bus's Betriebserlaubnis; the identity of Vienna's scan-car vendor; Michelin's
                  reason for quitting; the Commission's guidance on behaviour analytics under Article 5.
                </li>
              </>
            )}
          </ul>
        </section>
      </div>
    </div>
  );
}

export default function DanielReport() {
  const location = useLocation();
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function tryUnlock() {
      try {
        if (localStorage.getItem(STORAGE_KEY) === "true") {
          if (!cancelled) setUnlocked(true);
          return;
        }
      } catch (e) {}

      const params = new URLSearchParams(location.search);
      const key = params.get("key");
      if (key) {
        const hash = await sha256Hex(key);
        if (hash === PASSWORD_HASH) {
          try {
            localStorage.setItem(STORAGE_KEY, "true");
          } catch (e) {}
          if (!cancelled) setUnlocked(true);
        }
      }
      if (!cancelled) setChecking(false);
    }

    tryUnlock();
    return () => {
      cancelled = true;
    };
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hash = await sha256Hex(input);
    if (hash === PASSWORD_HASH) {
      try {
        localStorage.setItem(STORAGE_KEY, "true");
      } catch (e) {}
      setUnlocked(true);
      setError("");
    } else {
      setError("That password isn't right.");
    }
  };

  if (unlocked) {
    return <ReportBody />;
  }

  return (
    <div>
      <Helmet>
        <title>Private | Helia Jamshidi</title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
      </Helmet>
      {!checking && (
        <section className="speaking-section-colored dr-gate">
          <h2>This page is private</h2>
          <p>Enter the password you were given to view it.</p>
          <form onSubmit={handleSubmit}>
            <input
              type="password"
              placeholder="Password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="form-input"
              autoFocus
            />
            {error && <div className="error">{error}</div>}
            <button type="submit" className="submit-button">
              View
            </button>
          </form>
        </section>
      )}
    </div>
  );
}
